// Hreflang configuration for international SEO
// Simple implementation following the original pattern

import { addLanguageToPath, SSR_HIDDEN_LANGS } from "@Utils/languageCore";
import { SUPPORTED_LANG_VALUES } from "locales/langConfig/langConfig";

const LANGUAGE_PREFIXES = SUPPORTED_LANG_VALUES.filter((code) => !["en", "zh"].includes(code));

/**
 * Locales that must never appear in a `hreflang` annotation on .com.
 *
 * - zh-tw / zh-hk — excluded to avoid cross-domain references to the .cn site.
 * - vi / th / tr — launched noindex and absent from every sitemap; advertising them via
 *   hreflang would hand crawlers URLs we have deliberately kept out of discovery.
 *
 * This is NOT `NOINDEX_LANG_PREFIXES`. That list also contains fr/es/de, which ARE
 * hreflang-annotated here (they are sitemapped and were launched that way). Adding fr/es/de
 * to this list would change their live SEO. Keep the two concepts separate.
 */
const HREFLANG_EXCLUDED_LANGS = ["zh-tw", "zh-hk", ...SSR_HIDDEN_LANGS];

const HREFLANG_LANGUAGES = LANGUAGE_PREFIXES.filter(
  (code) => !HREFLANG_EXCLUDED_LANGS.includes(code),
);

/**
 * Generate canonical URL with language prefix for current language
 * - For China app: Use .cn domain
 * - For fr/es/de: Add language prefix to path
 * - For en/zh: Keep as-is
 */
export function generateCanonicalUrl(canonicalUrl: string, userLanguage?: string): string {
  if (!canonicalUrl) return canonicalUrl;

  let canonicalHref = canonicalUrl;

  if (userLanguage && !["en", "zh"].includes(userLanguage)) {
    // For fr/es/de, add language prefix to the path
    const urlMatch = canonicalHref.match(/^(https?:\/\/[^/]+)(\/.*)?$/);
    if (urlMatch) {
      const domain = urlMatch[1];
      const path = urlMatch[2] || "/";
      canonicalHref = `${domain}${addLanguageToPath(path, userLanguage)}`;
    }
  } else if (userLanguage && userLanguage === "zh") {
    canonicalHref = canonicalHref.replace(".com", ".cn");
  }

  return canonicalHref;
}

/**
 * Generate hreflang URLs for all supported languages
 * - For .cn (China app): Returns empty array (no hreflang annotations)
 * - For .com (global): Returns en, fr, es, de, and x-default (no zh-Hans cross-domain reference,
 *   and no vi/th/tr — see HREFLANG_EXCLUDED_LANGS)
 */
function generateHreflangUrls(url: string) {
  if (!url) return [];

  // Normalize: convert .cn to .com
  let baseUrl = url.replace(".cn", ".com");

  // Strip existing language prefix from path (e.g., /fr/places -> /places)
  for (const prefix of LANGUAGE_PREFIXES) {
    const prefixWithSlash = `/${prefix}/`;
    const domainPrefixIndex = baseUrl.indexOf(prefixWithSlash);

    if (domainPrefixIndex !== -1) {
      const beforePrefix = baseUrl.substring(0, domainPrefixIndex);
      if (beforePrefix.match(/^https?:\/\/[^/]+$/)) {
        // to skip the entire language prefix including the first slash, but keep the ending slash
        baseUrl = beforePrefix + baseUrl.substring(domainPrefixIndex + prefixWithSlash.length - 1);
        break;
      }
    }
  }

  // Clean up any double slashes (except protocol)
  baseUrl = baseUrl.replace(/([^:])\/\//g, "$1/");

  // Generate URLs
  const enUrl = baseUrl;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const zhUrl = baseUrl.replace(".com", ".cn");

  // Insert language prefix after domain
  const insertPrefix = (targetUrl: string, prefix: string) => {
    const domainMatch = targetUrl.match(/^(https?:\/\/[^/]+)(\/.*)?$/);
    if (domainMatch) {
      const domain = domainMatch[1];
      const path = domainMatch[2] || "/";
      return `${domain}${prefix}${path}`;
    }
    return targetUrl;
  };

  // Note: zh-Hans hreflang pointing to .cn is intentionally excluded
  // to avoid cross-domain hreflang references between .com and .cn
  return [
    { hreflang: "en", href: enUrl },
    ...HREFLANG_LANGUAGES.map((langCode) => ({
      hreflang: langCode,
      href: insertPrefix(baseUrl, `/${langCode}`),
    })),
    { hreflang: "x-default", href: enUrl },
  ];
}

export default generateHreflangUrls;
